import React, { useMemo, useState } from 'react';
import { Bar, BarChart, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { AnimatePresence, motion } from 'framer-motion';
import { BuildingIcon, CircleDollarSignIcon, CreditCardIcon, LandmarkIcon, PercentIcon, PlusIcon, TruckIcon, WalletIcon } from 'lucide-react';
import { brandEconomics, cashPosition, costStructure, financialAccounts, leaseAccounts, monthlyFinancials, partnerAccounts } from '../data/financeData';
const currency = (value: number) => `$${Math.round(value).toLocaleString('en-US')}`;
const compact = (value: number) => value >= 1_000_000 ? `$${(value / 1_000_000).toFixed(2)}M` : `$${Math.round(value / 1000)}K`;
type Period = 'Quarter to date' | 'Year to date';
export function FinanceDashboard() {
  const [period, setPeriod] = useState<Period>('Year to date');
  const months = period === 'Year to date' ? monthlyFinancials : monthlyFinancials.slice(-3);
  const totals = useMemo(() => {
    const grossRecovery = months.reduce((sum, month) => sum + month.grossRecovery, 0);
    const netRevenue = months.reduce((sum, month) => sum + month.netRevenue, 0);
    const operatingCosts = months.reduce((sum, month) => sum + month.operatingCosts, 0);
    const payouts = months.reduce((sum, month) => sum + month.brandPayouts, 0);
    return {
      grossRecovery,
      netRevenue,
      operatingCosts,
      payouts,
      operatingIncome: netRevenue - operatingCosts,
      takeRate: grossRecovery > 0 ? netRevenue / grossRecovery * 100 : 0
    };
  }, [months]);
  const chartData = months.map(month => ({
    ...month,
    margin: Number(((month.netRevenue - month.operatingCosts) / month.netRevenue * 100).toFixed(1))
  }));
  const costTotal = costStructure.reduce((sum, line) => sum + line.amount, 0);
  const partnerOutstanding = partnerAccounts.reduce((sum, account) => sum + account.outstanding, 0);
  const rentTotal = leaseAccounts.reduce((sum, lease) => sum + lease.monthlyRent, 0);
  return <div className="mx-auto max-w-7xl px-5 py-10 sm:px-8 lg:py-14" aria-labelledby="finance-heading" data-id="element-1282" data-mp="24exd021bfjtw">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between" data-id="element-1283" data-mp="1zg46itkg4t38">
        <div data-id="element-1284" data-mp="2dfn1sls5s9y4">
          <span className="inline-flex items-center gap-2 rounded-full bg-forest/8 px-3 py-1.5 text-xs font-semibold text-forest" data-id="element-1285" data-mp="2dfdex72euiwn">
            <LandmarkIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1286" data-mp="28htz42q2dy2l" />
            Finance
          </span>
          <h1 id="finance-heading" className="mt-3 font-display text-4xl font-medium tracking-tight sm:text-5xl" data-id="element-1287" data-mp="2idol97thyba4">
            Company financials
          </h1>
          <p className="mt-3 max-w-xl text-sm leading-relaxed text-ink/60" data-id="element-1288" data-mp="2w4jxmu37faig">
            Consolidated performance across every brand, recovery channel, 3PL partner, and facility.
          </p>
        </div>
        <div className="flex gap-1 self-start rounded-xl border border-ink/10 bg-cream p-1 sm:self-auto" data-id="element-1289" data-mp="363r7v8en6p1c">
          {(['Quarter to date', 'Year to date'] as const).map(item => <button key={item} type="button" onClick={() => setPeriod(item)} aria-pressed={period === item} className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${period === item ? 'bg-[#BB5E40] text-cream' : 'text-ink/60 hover:text-ink'}`} data-id="element-1290" data-mp="3kr9ienevydvo">
              {item}
            </button>)}
        </div>
      </div>

      <section className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" aria-label="Financial summary" data-id="element-1291" data-mp="36px2gbzq6c9s">
        <HeadlineMetric icon={CircleDollarSignIcon} label="Gross recovery" value={compact(totals.grossRecovery)} detail={`${currency(totals.payouts)} paid to brands`} data-id="element-1292" data-mp="2s1j2q9xlv3xg" />
        <HeadlineMetric icon={WalletIcon} label="Net revenue" value={compact(totals.netRevenue)} detail="ManyCo share after brand payouts" data-id="element-1293" data-mp="2j0cjht3knf0i" />
        <HeadlineMetric icon={PercentIcon} label="Blended take rate" value={`${totals.takeRate.toFixed(1)}%`} detail="Net revenue over gross recovery" data-id="element-1294" data-mp="2fzj2jgk4maj4" />
        <HeadlineMetric icon={LandmarkIcon} label="Operating income" value={compact(totals.operatingIncome)} detail={`${currency(totals.operatingCosts)} in operating costs`} positive={totals.operatingIncome > 0} data-id="element-1295" data-mp="268oeq7r8f2vk" />
      </section>

      <section className="mt-5 rounded-3xl border border-ink/10 bg-paper p-5 shadow-sm sm:p-7" aria-label="Revenue and margin" data-id="element-1296" data-mp="1s9os15na4y21">
        <h2 className="text-lg font-semibold" data-id="element-1297" data-mp="1nbdi6uwzinjt">Revenue and operating margin</h2>
        <p className="mt-1 text-sm text-ink/60" data-id="element-1298" data-mp="1ibr3eh1icz8w">Net revenue against operating costs, with margin plotted on top.</p>
        <div className="mt-6 h-72" data-id="element-1299" data-mp="3hnadx02gmfko">
          <ResponsiveContainer width="100%" height="100%" data-id="element-1300" data-mp="3hm92uvlg1t2y">
            <ComposedChart data={chartData} margin={{
            top: 8,
            right: 8,
            left: -15,
            bottom: 0
          }} barCategoryGap="24%" barGap={6} data-id="element-1301" data-mp="3cqblqn2cwip4">
              <CartesianGrid vertical={false} stroke="#1C1A17" strokeOpacity={0.1} strokeDasharray="3 5" data-id="element-1302" data-mp="3l60uavxc725y" />
              <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{
              fill: '#6D6861',
              fontSize: 12
            }} dy={8} data-id="element-1303" data-mp="3p5rrpihdcgjg" />
              <YAxis yAxisId="left" axisLine={false} tickLine={false} tick={{
              fill: '#6D6861',
              fontSize: 12
            }} tickFormatter={(value: number) => `$${Math.round(value / 1000)}k`} data-id="element-1304" data-mp="2sa18quidic4" />
              <YAxis yAxisId="right" orientation="right" axisLine={false} tickLine={false} tick={{
              fill: '#6D6861',
              fontSize: 12
            }} tickFormatter={(value: number) => `${value}%`} data-id="element-1305" data-mp="369a154e9amos" />
              <Tooltip content={<FinanceTooltip data-id="element-1307" data-mp="qjyr80juxupw" />} cursor={{
              fill: '#BB5E40',
              fillOpacity: 0.06
            }} data-id="element-1306" data-mp="qhm8vma6ticb" />
              <Bar yAxisId="left" dataKey="netRevenue" name="Net revenue" fill="#BB5E40" radius={[6, 6, 0, 0]} maxBarSize={54} data-id="element-1308" data-mp="vf498oa52p8p" />
              <Bar yAxisId="left" dataKey="operatingCosts" name="Operating costs" fill="#C9BFAE" radius={[6, 6, 0, 0]} maxBarSize={54} data-id="element-1309" data-mp="tqm2k4han7x" />
              <Line yAxisId="right" type="monotone" dataKey="margin" name="Operating margin" stroke="#2E7D51" strokeWidth={2.5} dot={{
              r: 3,
              fill: '#2E7D51'
            }} data-id="element-1310" data-mp="fpy3jcoi5aji" />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-4 flex flex-wrap gap-4 text-xs text-ink/60" data-id="element-1311" data-mp="3j7zgg08ttbgj">
          <Legend color="#BB5E40" label="Net revenue" data-id="element-1312" data-mp="3e9oba2th7p4n" />
          <Legend color="#C9BFAE" label="Operating costs" data-id="element-1313" data-mp="3sxj21dnxbjf3" />
          <Legend color="#2E7D51" label="Operating margin" data-id="element-1314" data-mp="3sz2h50xufofa" />
        </div>
      </section>

      <section className="mt-5 grid gap-5 lg:grid-cols-[1.25fr_0.75fr]" data-id="element-1315" data-mp="3o6130v5gnswr">
        <article className="min-w-0 rounded-2xl border border-ink/10 bg-paper p-5 sm:p-6" aria-labelledby="brand-economics-heading" data-id="element-1316" data-mp="30gf325h9l9np">
          <h2 id="brand-economics-heading" className="text-base font-semibold" data-id="element-1317" data-mp="2vhlw6vwhggwi">Brand economics</h2>
          <p className="mt-1 text-sm text-ink/60" data-id="element-1318" data-mp="1ayi35tgqfl8g">Where recovery revenue is generated and what it contributes.</p>
          <div className="mt-5 -mx-2 overflow-x-auto px-2" data-id="element-1319" data-mp="1fsw4sdlzmolk">
            <table className="w-full min-w-[620px] text-left text-xs" data-id="element-1320" data-mp="112yb5i5ah6oz">
              <thead className="border-b border-ink/10 text-ink/50" data-id="element-1321" data-mp="160qcf2rtq55u">
                <tr data-id="element-1322" data-mp="15yx3fgtqn8uo">
                  <th scope="col" className="pb-2 font-semibold" data-id="element-1323" data-mp="1tqu72kn1aukp">Brand</th>
                  <th scope="col" className="pb-2 text-right font-semibold" data-id="element-1324" data-mp="1tohu6lw4vaei">Gross recovery</th>
                  <th scope="col" className="pb-2 text-right font-semibold" data-id="element-1325" data-mp="1ynkw3xgth0i9">Brand payout</th>
                  <th scope="col" className="pb-2 text-right font-semibold" data-id="element-1326" data-mp="1kpmhqs2hx42r">Net revenue</th>
                  <th scope="col" className="pb-2 text-right font-semibold" data-id="element-1327" data-mp="1opoceh9tcx8j">Contribution</th>
                  <th scope="col" className="pb-2 text-right font-semibold" data-id="element-1328" data-mp="9962ozuuspgf">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink/8" data-id="element-1329" data-mp="d9zq7s4dw28s">
                {brandEconomics.map(brand => <tr key={brand.brand} data-id="element-1330" data-mp="100srqfneorhv">
                    <th scope="row" className="py-3 pr-3 font-semibold text-ink" data-id="element-1331" data-mp="zzrdy3f09qwg">{brand.brand}</th>
                    <td className="py-3 text-right tabular-nums text-ink/70" data-id="element-1332" data-mp="w08uzd6qvpw9">{currency(brand.grossRecovery)}</td>
                    <td className="py-3 text-right tabular-nums text-ink/70" data-id="element-1333" data-mp="r1p89akbua2u">{currency(brand.brandPayout)}</td>
                    <td className="py-3 text-right font-semibold tabular-nums text-ink" data-id="element-1334" data-mp="1nplr6m38lhh8">{currency(brand.netRevenue)}</td>
                    <td className="py-3 text-right font-semibold tabular-nums text-positive" data-id="element-1335" data-mp="1jr5yq2odzgwf">{brand.contributionMargin.toFixed(1)}%</td>
                    <td className="py-3 text-right" data-id="element-1336" data-mp="19zs345zk318k">
                      <span className={`inline-flex whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold ${brand.status === 'Active' ? 'bg-positive/10 text-positive' : brand.status === 'Onboarding' ? 'bg-[#C9A04E]/15 text-[#8B6A20]' : 'bg-ink/7 text-ink/55'}`} data-id="element-1337" data-mp="19ww4effvw8j2">
                        {brand.status}
                      </span>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </article>

        <article className="min-w-0 rounded-2xl border border-ink/10 bg-paper p-5 sm:p-6" aria-labelledby="cost-structure-heading" data-id="element-1338" data-mp="2svk9l94v4t1n">
          <h2 id="cost-structure-heading" className="text-base font-semibold" data-id="element-1339" data-mp="32npxo7rzuqbk">Cost structure</h2>
          <p className="mt-1 text-sm text-ink/60" data-id="element-1340" data-mp="37mb26jea3w5t">{currency(costTotal)} year to date.</p>
          <ul className="mt-5 space-y-4" data-id="element-1341" data-mp="3bo651sbuyhlp">
            {costStructure.map(line => {
            const share = Math.round(line.amount / costTotal * 100);
            return <li key={line.category} data-id="element-1342" data-mp="3gkwgu3gmqr1b">
                  <div className="flex items-baseline justify-between gap-3" data-id="element-1343" data-mp="3q8qx5kh88oet">
                    <span className="text-xs font-semibold text-ink" data-id="element-1344" data-mp="3v6sn8pd2lnzs">{line.category}</span>
                    <span className="shrink-0 text-xs font-semibold tabular-nums text-ink/70" data-id="element-1345" data-mp="40r742e9047a">{currency(line.amount)}</span>
                  </div>
                  <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-cream" data-id="element-1346" data-mp="8s9asy7noivy">
                    <div className="h-full rounded-full bg-[#BB5E40]" style={{
                  width: `${share}%`
                }} data-id="element-1347" data-mp="cs1d4t6ghssv" />
                  </div>
                  <p className="mt-1 text-[11px] text-ink/50" data-id="element-1348" data-mp="hqb7x2o9qrdy">{line.detail} · {share}% of spend</p>
                </li>;
          })}
          </ul>
        </article>
      </section>

      <section className="mt-5 grid gap-5 lg:grid-cols-2" data-id="element-1349" data-mp="rptr3gwcu94y">
        <article className="min-w-0 rounded-2xl border border-ink/10 bg-paper p-5 sm:p-6" aria-labelledby="partner-accounts-heading" data-id="element-1350" data-mp="2oj5dbm4t6jq2">
          <div className="flex items-center gap-2" data-id="element-1351" data-mp="2ti8f8xphs9tt">
            <TruckIcon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-1352" data-mp="1amc4o0siqsfc" />
            <h2 id="partner-accounts-heading" className="text-base font-semibold" data-id="element-1353" data-mp="1aoeykvwsl5pc">3PL partner accounts</h2>
          </div>
          <p className="mt-1 text-sm text-ink/60" data-id="element-1354" data-mp="11tqwtfqfe65a">{currency(partnerOutstanding)} outstanding across {partnerAccounts.length} partners.</p>
          <div className="mt-5 -mx-2 overflow-x-auto px-2" data-id="element-1355" data-mp="wwqqr4tkt3e6">
            <table className="w-full min-w-[520px] text-left text-xs" data-id="element-1356" data-mp="1tlyemjho3ol9">
              <thead className="border-b border-ink/10 text-ink/50" data-id="element-1357" data-mp="1tj3s8mf3ftp7">
                <tr data-id="element-1358" data-mp="1pkcse7j8txye">
                  <th scope="col" className="pb-2 font-semibold" data-id="element-1359" data-mp="1phqpwmb7m8n1">Partner</th>
                  <th scope="col" className="pb-2 text-right font-semibold" data-id="element-1360" data-mp="1kj6za8lo88oc">Monthly</th>
                  <th scope="col" className="pb-2 text-right font-semibold" data-id="element-1361" data-mp="1fkvptyzag06n">Outstanding</th>
                  <th scope="col" className="pb-2 text-right font-semibold" data-id="element-1362" data-mp="96mq96bopjre">Terms</th>
                  <th scope="col" className="pb-2 text-right font-semibold" data-id="element-1363" data-mp="468no3sur20r">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink/8" data-id="element-1364" data-mp="5o70hu9l8dpf">
                {partnerAccounts.map(account => <tr key={account.partner} data-id="element-1365" data-mp="alz2xi7uzi7v">
                    <th scope="row" className="py-3 pr-3" data-id="element-1366" data-mp="3sswpg79w147m">
                      <span className="block font-semibold text-ink" data-id="element-1367" data-mp="3suhhnq8qcb2b">{account.partner}</span>
                      <span className="text-[11px] font-normal text-ink/50" data-id="element-1368" data-mp="1k959undvn1n">{account.location}</span>
                    </th>
                    <td className="py-3 text-right tabular-nums text-ink/70" data-id="element-1369" data-mp="3ntabtwq5dlya">{currency(account.monthlyCost)}</td>
                    <td className="py-3 text-right font-semibold tabular-nums text-ink" data-id="element-1370" data-mp="3a25fb3i2t9s6">{account.outstanding > 0 ? currency(account.outstanding) : '—'}</td>
                    <td className="py-3 text-right tabular-nums text-ink/60" data-id="element-1371" data-mp="3e3qq8jb1vdzq">{account.terms}</td>
                    <td className="py-3 text-right" data-id="element-1372" data-mp="304hlqgw7i8bl">
                      <span className={`inline-flex whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold ${account.status === 'Current' ? 'bg-positive/10 text-positive' : account.status === 'Due soon' ? 'bg-[#C9A04E]/15 text-[#8B6A20]' : 'bg-[#BB5E40]/12 text-[#94452E]'}`} data-id="element-1373" data-mp="353c6hodqi6yc">
                        {account.status}
                      </span>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </article>

        <article className="min-w-0 rounded-2xl border border-ink/10 bg-paper p-5 sm:p-6" aria-labelledby="lease-accounts-heading" data-id="element-1374" data-mp="3e4mu8chl521s">
          <div className="flex items-center gap-2" data-id="element-1375" data-mp="397d753ho52h4">
            <BuildingIcon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-1376" data-mp="3n5l255v59tpn" />
            <h2 id="lease-accounts-heading" className="text-base font-semibold" data-id="element-1377" data-mp="3j516y7u4t065">Facilities and rent</h2>
          </div>
          <p className="mt-1 text-sm text-ink/60" data-id="element-1378" data-mp="1givrjc4aa8vv">{currency(rentTotal)} per month across {leaseAccounts.length} sites.</p>
          <ul className="mt-5 divide-y divide-ink/8" data-id="element-1379" data-mp="1lfvxln14vbmz">
            {leaseAccounts.map(lease => <li key={lease.site} className="flex items-start justify-between gap-4 py-3.5 first:pt-0 last:pb-0" data-id="element-1380" data-mp="1q98dn8rqya44">
                <div className="min-w-0" data-id="element-1381" data-mp="1v67158gwnitc">
                  <p className="text-sm font-semibold text-ink" data-id="element-1382" data-mp="xjrm0dbyax8y">{lease.site}</p>
                  <p className="mt-0.5 text-xs text-ink/55" data-id="element-1383" data-mp="12ib94h2a6f2w">{lease.purpose}</p>
                  <p className="mt-0.5 text-[11px] text-ink/45" data-id="element-1384" data-mp="12kfaoscpj200">{lease.sqft.toLocaleString('en-US')} sq ft · term ends {lease.termEnds}</p>
                </div>
                <span className="shrink-0 font-semibold tabular-nums text-ink" data-id="element-1385" data-mp="1bk2d9hh6adun">{currency(lease.monthlyRent)}</span>
              </li>)}
          </ul>
        </article>
      </section>

      <CashPositionSection data-id="element-1386" data-mp="2mxels5nvnkn5" />
    </div>;
}
function CashPositionSection() {
  const [accounts, setAccounts] = useState(financialAccounts);
  const [connecting, setConnecting] = useState(false);
  const connectedBalance = accounts.reduce((sum, account) => sum + account.balance, 0);
  const needsAttention = accounts.filter(account => account.status === 'Needs reauth').length;
  return <section className="mt-5 rounded-3xl border border-ink/10 bg-paper p-5 shadow-sm sm:p-7" aria-labelledby="cash-position-heading" data-id="element-1387" data-mp="3tpbtvn16pfva">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between" data-id="element-1388" data-mp="3orad58r8dsmz">
        <div data-id="element-1389" data-mp="3jpuv7pkco28t">
          <div className="flex items-center gap-2" data-id="element-1390" data-mp="3jpb8zwno2roz">
            <WalletIcon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-1391" data-mp="3foj0j5mmcqtr" />
            <h2 id="cash-position-heading" className="text-lg font-semibold" data-id="element-1392" data-mp="3apy138f6yb6d">Cash position</h2>
          </div>
          <p className="mt-1 text-sm text-ink/60" data-id="element-1393" data-mp="1dxed6lrpycf8">
            {currency(connectedBalance)} net across {accounts.length} connected accounts
            {needsAttention > 0 ? ` · ${needsAttention} needs reauthorization` : ''}.
          </p>
        </div>
        <button type="button" onClick={() => setConnecting(open => !open)} aria-expanded={connecting} className="inline-flex shrink-0 items-center gap-1.5 self-start rounded-full bg-[#BB5E40] px-4 py-2 text-xs font-semibold text-cream transition-colors hover:bg-[#a4512f]" data-id="element-1394" data-mp="1s1wrlffn06u3">
          <PlusIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1395" data-mp="1nz8lhfib5e0t" />
          Connect an account
        </button>
      </div>

      <AnimatePresence initial={false} data-id="element-1396" data-mp="2ftalvqxcmchp">
        {connecting && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: 0.24,
        ease: [0.23, 1, 0.32, 1]
      }} className="overflow-hidden" data-id="element-1397" data-mp="2fqg11yafapnr">
            <div className="mt-5 rounded-2xl border border-ink/10 bg-cream/45 p-5" data-id="element-1398" data-mp="qfxc3m6gvw97">
              <h3 className="text-sm font-semibold" data-id="element-1399" data-mp="veiadg25s5v0">Choose an account type</h3>
              <p className="mt-1 text-xs text-ink/55" data-id="element-1400" data-mp="nsd29w9zdegg">Balances and transactions sync automatically once authorized.</p>
              <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-4" data-id="element-1401" data-mp="ittlsbe5ouvh">
                {([{
              label: 'Bank account',
              detail: 'Checking or savings',
              icon: LandmarkIcon
            }, {
              label: 'Credit card',
              detail: 'Corporate spend',
              icon: CreditCardIcon
            }, {
              label: 'Payment processor',
              detail: 'Stripe, Shopify Payments',
              icon: CircleDollarSignIcon
            }, {
              label: 'Accounting system',
              detail: 'QuickBooks, NetSuite',
              icon: BuildingIcon
            }] as const).map(option => <button key={option.label} type="button" className="rounded-xl border border-ink/12 bg-paper p-4 text-left transition-colors hover:border-[#BB5E40]/45 hover:bg-[#BB5E40]/5 focus:outline-none focus:ring-2 focus:ring-[#BB5E40]/25" data-id="element-1402" data-mp="92ys4lykyt00">
                    <option.icon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-1403" data-mp="2s1rrgpa21sec" />
                    <p className="mt-3 text-sm font-semibold text-ink" data-id="element-1404" data-mp="2w339o9qlnbci">{option.label}</p>
                    <p className="mt-0.5 text-[11px] text-ink/50" data-id="element-1405" data-mp="30ysb6im6j2bg">{option.detail}</p>
                  </button>)}
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>

      <div className="mt-6 grid gap-5 lg:grid-cols-[1fr_1fr]" data-id="element-1406" data-mp="3awgfo09l7o6t">
        <div className="min-w-0" data-id="element-1407" data-mp="3eybhd5vfk3l4">
          <h3 className="text-sm font-semibold" data-id="element-1408" data-mp="3exibe74api41">Connected accounts</h3>
          <ul className="mt-3 divide-y divide-ink/8" data-id="element-1409" data-mp="3jrd0jq2o6pai">
            {accounts.map(account => <li key={account.mask} className="flex items-start justify-between gap-4 py-3.5 first:pt-0" data-id="element-1410" data-mp="3oowe0nrjmy1e">
                <div className="min-w-0" data-id="element-1411" data-mp="3tqlnw03155nw">
                  <p className="text-sm font-semibold text-ink" data-id="element-1412" data-mp="2hy43okftqig">{account.name}</p>
                  <p className="mt-0.5 text-xs text-ink/55" data-id="element-1413" data-mp="1n0ifzcid41tj">
                    {account.institution} · {account.type} ····{account.mask}
                  </p>
                  <p className="mt-0.5 text-[11px] text-ink/45" data-id="element-1414" data-mp="1hzugs3jzceo8">Synced {account.lastSynced}</p>
                </div>
                <div className="shrink-0 text-right" data-id="element-1415" data-mp="1d4f3bcpvwksc">
                  <p className={`font-semibold tabular-nums ${account.balance < 0 ? 'text-[#94452E]' : 'text-ink'}`} data-id="element-1416" data-mp="184awkees34fb">
                    {account.balance < 0 ? `−${currency(Math.abs(account.balance))}` : currency(account.balance)}
                  </p>
                  {account.status === 'Needs reauth' ? <button type="button" onClick={() => setAccounts(current => current.map(item => item.mask === account.mask ? {
                ...item,
                status: 'Connected',
                lastSynced: 'just now'
              } : item))} className="mt-1 inline-flex whitespace-nowrap rounded-full bg-[#BB5E40]/12 px-2 py-0.5 text-[11px] font-semibold text-[#94452E] transition-colors hover:bg-[#BB5E40]/20" data-id="element-1417" data-mp="1vzvpg56hkqe9">
                      Reconnect
                    </button> : <span className="mt-1 inline-flex items-center gap-1 whitespace-nowrap text-[11px] font-semibold text-positive" data-id="element-1418" data-mp="7rd93r5aglg">
                      <span className="h-1.5 w-1.5 rounded-full bg-positive" aria-hidden="true" data-id="element-1419" data-mp="wytenbo6hrml" />
                      Connected
                    </span>}
                </div>
              </li>)}
          </ul>
        </div>

        <div className="min-w-0" data-id="element-1420" data-mp="j4947iy66cgn">
          <h3 className="text-sm font-semibold" data-id="element-1421" data-mp="j37qt7toldvr">Working capital</h3>
          <dl className="mt-3 grid gap-3 sm:grid-cols-2" data-id="element-1422" data-mp="1glhk2i9nfiys">
            {cashPosition.map(position => <div key={position.label} className="rounded-xl border border-ink/10 bg-cream/45 px-4 py-3" data-id="element-1423" data-mp="1bt7p2ntofspn">
                <dt className="text-[11px] font-semibold uppercase tracking-wider text-ink/50" data-id="element-1424" data-mp="1j6p08xr0m1yn">{position.label}</dt>
                <dd className={`mt-1 font-display text-xl font-semibold tracking-tight ${position.tone === 'in' ? 'text-positive' : position.tone === 'out' ? 'text-[#94452E]' : 'text-ink'}`} data-id="element-1425" data-mp="1o6bdv8ar9k7z">
                  {currency(position.amount)}
                </dd>
                <p className="mt-0.5 text-[11px] text-ink/50" data-id="element-1426" data-mp="272t9w1wn06a1">{position.detail}</p>
              </div>)}
          </dl>
        </div>
      </div>
    </section>;
}
function HeadlineMetric({
  icon: Icon,
  label,
  value,
  detail,
  positive = false
}: {
  icon: typeof LandmarkIcon;
  label: string;
  value: string;
  detail: string;
  positive?: boolean;
}) {
  return <article className="rounded-2xl border border-ink/10 bg-paper p-5" data-id="element-1427" data-mp="2ptkhaxx2dmls">
      <Icon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-1428" data-mp="2ptu82npxnxsj" />
      <p className="mt-4 text-sm text-ink/55" data-id="element-1429" data-mp="psctoawlhszv">{label}</p>
      <p className={`mt-1 font-display text-3xl font-semibold tracking-tight ${positive ? 'text-positive' : 'text-ink'}`} data-id="element-1430" data-mp="lppzi3cgbvzi">{value}</p>
      <p className="mt-1 text-xs text-ink/50" data-id="element-1431" data-mp="zhe7bufo3yao">{detail}</p>
    </article>;
}
function Legend({
  color,
  label
}: {
  color: string;
  label: string;
}) {
  return <span className="inline-flex items-center gap-2" data-id="element-1432" data-mp="uol45azuky0p">
      <span className="h-2.5 w-2.5 rounded-full" style={{
      backgroundColor: color
    }} data-id="element-1433" data-mp="19dzeomf5tlho" />
      {label}
    </span>;
}
function FinanceTooltip({
  active,
  payload,
  label
}: {
  active?: boolean;
  payload?: Array<{
    name?: string;
    value?: number;
    color?: string;
  }>;
  label?: string;
}) {
  if (!active || !payload?.length) return null;
  return <div className="rounded-xl border border-ink/10 bg-paper px-3 py-2 text-xs shadow-lg" data-id="element-1434" data-mp="1det6hqvq3kfu">
      <p className="font-semibold text-ink" data-id="element-1435" data-mp="3k9g26f2kjkds">{label}</p>
      <ul className="mt-1.5 space-y-1" data-id="element-1436" data-mp="3f926zxm2g4x6">
        {payload.map(entry => <li key={entry.name} className="flex items-center gap-2 text-ink/70" data-id="element-1437" data-mp="1uqhwaczvnzno">
            <span className="h-2 w-2 rounded-full" style={{
          backgroundColor: entry.color
        }} data-id="element-1438" data-mp="1uskkqsldtkqa" />
            <span data-id="element-1439" data-mp="1zr5kys0mw4eq">{entry.name}</span>
            <span className="ml-auto font-semibold tabular-nums text-ink" data-id="element-1440" data-mp="1lypoyb65h6nb">
              {entry.name === 'Operating margin' ? `${entry.value}%` : currency(Number(entry.value ?? 0))}
            </span>
          </li>)}
      </ul>
    </div>;
}